"use client";

import { useState } from "react";
import ActionMenu from "@/app/(components)/generics/ActionMenu/ActionMenu";
import IconArrowDown from "@/app/(components)/generics/Icons/IconArrowDown";
import IconArrowUp from "@/app/(components)/generics/Icons/IconArrowUp";
import IconEdit from "@/app/(components)/generics/Icons/IconEdit";
import IconGoal from "@/app/(components)/generics/Icons/IconGoal";
import IconPlus from "@/app/(components)/generics/Icons/IconPlus";
import IconTrash from "@/app/(components)/generics/Icons/IconTrash";
import Popover from "@/app/(components)/generics/Popover/Popover";
import PopoverContent from "@/app/(components)/generics/Popover/PopoverContent";
import PopoverTrigger from "@/app/(components)/generics/Popover/PopoverTrigger";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { SequenceObjectiveData } from "@/app/types/messages-content/SequenceObjectiveData";
import { UStyles } from "@/app/utils/UStyles";
import styles from "./ObjectiveSelector.module.css";

type Props = {
  objectives: SequenceObjectiveData[];
  objectiveId?: number;
  onChange: (objectiveId: number) => void;
  onEdit?: (objective: SequenceObjectiveData) => void;
  onDelete?: (objective: SequenceObjectiveData) => void;
  onCreate?: () => void;
};

export default function ObjectiveSelector(props: Props) {
  const T = useT();
  const { objectives, objectiveId, onChange, onEdit, onDelete, onCreate } = props;

  const [isOpen, setIsOpen] = useState(false);

  const current = objectives.find((objective) => objective.id === objectiveId) ?? objectives[0];

  if (!current) {
    return null;
  }

  const select = (objective: SequenceObjectiveData) => {
    setIsOpen(false);
    onChange(objective.id);
  };

  return (
    <div className={styles.container}>
      <span className={styles.label}>{T(TKey.MESSAGES_CONTENT_OBJECTIVE_LABEL)}</span>

      <div className={styles.row}>
        <Popover
          isVisible={isOpen}
          setIsVisible={setIsOpen}
          openAfterClick={true}
          closeWhenClickOutside={true}
          isPortaled={true}
          className={styles.pillWrapper}
        >
          <PopoverTrigger>
            <div className={UStyles.classNames(styles.pill, isOpen && styles.pillOpen)}>
              <IconGoal />
              <span className={styles.pillLabel}>{current.label}</span>
              <span className={styles.pillArrow}>
                {isOpen ? <IconArrowUp /> : <IconArrowDown />}
              </span>
            </div>
          </PopoverTrigger>

          <PopoverContent position={{ default: { top: "8px", left: "0px" } }}>
            <div className={styles.popover}>
              {objectives.map((objective) => (
                <button
                  key={objective.id}
                  type="button"
                  className={UStyles.classNames(
                    styles.option,
                    objective.id === current.id && styles.optionActive,
                  )}
                  onClick={() => select(objective)}
                >
                  <span className={styles.optionLabel}>{objective.label}</span>
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>

        {(onEdit || onDelete) && (
          <div className={styles.menuAction}>
            <ActionMenu
              items={[
                onEdit && {
                  label: T(TKey.MESSAGES_CONTENT_OBJECTIVE_EDIT) as string,
                  icon: <IconEdit width={16} height={16} />,
                  onClick: () => onEdit(current),
                },
                onDelete && {
                  label: T(TKey.MESSAGES_CONTENT_OBJECTIVE_DELETE_CONFIRM) as string,
                  icon: <IconTrash size={16} />,
                  danger: true,
                  onClick: () => onDelete(current),
                },
              ]}
            />
          </div>
        )}

        {onCreate && (
          <button
            type="button"
            className={styles.actionBtn}
            aria-label={T(TKey.MESSAGES_CONTENT_OBJECTIVE_ADD) as string}
            onClick={onCreate}
          >
            <IconPlus />
          </button>
        )}
      </div>

      {current.description && <p className={styles.description}>{current.description}</p>}
    </div>
  );
}
